// Le protocole de blocage en 4 paliers (R-BLQ-01 à R-BLQ-08).
import { useEffect, useState } from 'react';
import type { ExerciceCompile } from '@partage/contenu';
import { useMagasin, magasin } from '../donnees/magasin';
import { Html } from './Html';

const DELAIS_MIN = { 2: 15, 3: 30 } as const;

function formaterAttente(ms: number): string {
  const min = Math.ceil(ms / 60000);
  return min <= 1 ? 'moins d’une minute' : `${min} min`;
}

export function Blocage({ exercice, session, prochaineSession }: { exercice: ExerciceCompile; session: string; prochaineSession?: string }) {
  const { etat } = useMagasin();
  const blocage = etat.blocages.find((b) => b.exercice === exercice.id && b.session === session);
  const [, tic] = useState(0);
  const [ouverts, setOuverts] = useState<number[]>([]);

  // Un rafraîchissement toutes les 30 s pour faire avancer les comptes à rebours (R-BLQ-02).
  useEffect(() => {
    if (!blocage || blocage.abandon_le) return;
    const t = window.setInterval(() => tic((n) => n + 1), 30_000);
    return () => window.clearInterval(t);
  }, [blocage?.t0, blocage?.abandon_le]);

  if (!blocage) {
    return (
      <div className="ligne" style={{ marginTop: '0.8em' }}>
        <button onClick={() => void magasin.emettre('blocage_declenche', { exercice: exercice.id, session })}>🧭 Je suis bloqué</button>
        <span className="petit-texte doux">Premier palier tout de suite : la doc précise + une question. Les suivants se débloquent avec le temps.</span>
      </div>
    );
  }

  const ecoule = Date.now() - new Date(blocage.t0).getTime();
  const disponible = (palier: 2 | 3) => ecoule >= DELAIS_MIN[palier] * 60000;
  const abandonne = Boolean(blocage.abandon_le);

  async function ouvrir(palier: number) {
    setOuverts((o) => (o.includes(palier) ? o : [...o, palier]));
    if (!blocage!.paliers.includes(palier)) await magasin.emettre('palier_consulte', { exercice: exercice.id, session, palier });
  }

  async function jeterLEponge() {
    if (!window.confirm('La solution sera expliquée ligne par ligne, et une variante de cet exercice sera ajoutée à ta prochaine session pour que tu la refasses seul. Continuer ?')) return;
    await magasin.emettre('abandon_exercice', { exercice: exercice.id, session, prochaine_session: prochaineSession });
    setOuverts((o) => [...o, 4]);
  }

  const palier = (n: 1 | 2 | 3, titre: string) => {
    const dispo = n === 1 || disponible(n);
    const vu = blocage.paliers.includes(n) || ouverts.includes(n);
    return (
      <div className={`palier ${dispo ? '' : 'verrouille'}`} key={n}>
        <h3>Palier {n} — {titre} {vu ? <span className="badge">vu</span> : null}</h3>
        {vu ? <Html html={exercice.sections[`palier_${n}`]} /> : dispo
          ? <button onClick={() => void ouvrir(n)}>Afficher</button>
          : <span className="doux petit-texte">disponible dans {formaterAttente(DELAIS_MIN[n as 2 | 3] * 60000 - ecoule)} — le temps de chercher par toi-même</span>}
      </div>
    );
  };

  return (
    <div className="bloque">
      <div className="ligne"><strong>Protocole de blocage</strong> <span className="petit-texte doux">déclenché à {new Date(blocage.t0).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span></div>
      {palier(1, 'la doc et une question')}
      {palier(2, 'un indice conceptuel')}
      {palier(3, 'la structure de la solution')}
      <div className={`palier ${abandonne ? '' : 'verrouille'}`}>
        <h3>Palier 4 — la solution expliquée {abandonne ? <span className="badge attention">éponge jetée</span> : null}</h3>
        {abandonne
          ? <><Html html={exercice.sections.palier_4} /><p className="petit-texte doux">Variante planifiée dans ta prochaine session (R-BLQ-06) : tu la referas seul.</p></>
          : <button className="danger" onClick={() => void jeterLEponge()}>Je jette l’éponge</button>}
      </div>
    </div>
  );
}
